import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';

import { Btn } from '../../components/Btn';
import { EmptyState } from '../../components/EmptyState';
import { Field } from '../../components/Field';
import { FormPanel } from '../../components/FormPanel';
import { Input } from '../../components/Input';
import { ReceiptPreview } from '../../components/ReceiptPreview';
import { getSettings } from '../../data/fetchers';
import { saveSettings } from '../../data/mutators';
import { canEditSystemConfig } from '../../lib/permissions';
import { useSession } from '../../stores/session';
import type { SystemSettings } from '../../types/domain';
import { SettingsHeader } from './SettingsHeader';

export function SystemSettingsView() {
  const user = useSession((s) => s.user);
  const canEdit = user ? canEditSystemConfig(user) : false;
  const { data, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: getSettings,
  });

  if (!canEdit) {
    return (
      <div className="bo-fade">
        <SettingsHeader
          title="ตั้งค่าระบบ"
          subtitle="ดูแลข้อมูลร้าน ภาษี และข้อความที่พิมพ์บนใบเสร็จ"
          status={{ label: 'คุณไม่มีสิทธิ์แก้ไข', tone: 'neutral' }}
        />
        <div className="bo-card">
          <EmptyState
            title="เฉพาะเจ้าของ"
            sub="การตั้งค่าระบบทำได้โดยเจ้าของหรือผู้ที่ได้รับสิทธิ์ตั้งค่าระบบ"
          />
        </div>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="bo-fade">
        <SettingsHeader
          title="ตั้งค่าระบบ"
          subtitle="ดูแลข้อมูลร้าน ภาษี และข้อความที่พิมพ์บนใบเสร็จ"
          status={{ label: 'กำลังโหลดการตั้งค่า', tone: 'neutral' }}
        />
        <div className="bo-card h-40 animate-pulse" />
      </div>
    );
  }

  return <SystemSettingsForm initialSettings={data} />;
}

function SystemSettingsForm({
  initialSettings,
}: {
  initialSettings: SystemSettings;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<SystemSettings>(initialSettings);
  const [saved, setSaved] = useState(false);

  const mutation = useMutation({
    mutationFn: saveSettings,
    onSuccess: (savedSettings) => {
      // Write the saved value straight into the cache: instant propagation to this view
      // + the VAT report (same ['settings'] key) with NO background refetch (avoids the
      // out-of-act re-render that invalidateQueries caused). Cross-tab still re-syncs via
      // the storage listener invalidating ['settings'] in the other tab.
      qc.setQueryData(['settings'], savedSettings);
      setSaved(true);
    },
  });

  const set = <K extends keyof SystemSettings>(k: K, v: SystemSettings[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    setSaved(false);
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    mutation.mutate(form);
  };

  return (
    <div className="bo-fade">
      <SettingsHeader
        title="ตั้งค่าระบบ"
        subtitle="ดูแลข้อมูลร้าน ภาษี และข้อความที่พิมพ์บนใบเสร็จ"
        status={
          mutation.isPending
            ? { label: 'กำลังบันทึก', tone: 'neutral' }
            : saved
              ? { label: 'บันทึกแล้ว', tone: 'ok' }
              : { label: 'พร้อมแก้ไข', tone: 'neutral' }
        }
      />
      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
        <form onSubmit={onSubmit} className="space-y-4">
          <FormPanel>
            <div className="mb-4">
              <h2 className="text-h3 font-bold text-ink">ข้อมูลร้าน</h2>
              <p className="mt-1 text-sm-label text-ink-3">
                ใช้เป็นข้อมูลผู้ขายในเอกสารและรายงานของระบบ
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="ชื่อร้าน" required>
                <Input
                  aria-label="ชื่อร้าน"
                  value={form.shopName}
                  onChange={(e) => set('shopName', e.target.value)}
                />
              </Field>
              <Field label="เลขผู้เสียภาษี" required>
                <Input
                  aria-label="เลขผู้เสียภาษี"
                  value={form.taxId}
                  onChange={(e) => set('taxId', e.target.value)}
                />
              </Field>
            </div>
          </FormPanel>

          <FormPanel>
            <div className="mb-4">
              <h2 className="text-h3 font-bold text-ink">ภาษีมูลค่าเพิ่ม</h2>
              <p className="mt-1 text-sm-label text-ink-3">
                ค่านี้มีผลกับการคำนวณราคาและรายงาน VAT
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 sm:items-end">
              <Field label="อัตรา VAT (%)" required>
                <Input
                  aria-label="อัตรา VAT (%)"
                  type="number"
                  value={Math.round(form.vatRate * 100)}
                  onChange={(e) => set('vatRate', Number(e.target.value) / 100)}
                />
              </Field>
              <label className="flex h-10 cursor-pointer select-none items-center gap-2.5 rounded-btn border border-line px-3">
                <input
                  aria-label="ราคารวม VAT แล้ว"
                  type="checkbox"
                  checked={form.vatInclusive}
                  onChange={(e) => set('vatInclusive', e.target.checked)}
                  className="h-4.5 w-4.5 cursor-pointer rounded border-line text-primary accent-primary focus:ring-primary/30"
                />
                <span className="text-sm-label font-semibold text-ink-2">
                  ราคารวม VAT แล้ว
                </span>
              </label>
            </div>
          </FormPanel>

          <FormPanel>
            <div className="mb-4">
              <h2 className="text-h3 font-bold text-ink">ข้อความบนใบเสร็จ</h2>
              <p className="mt-1 text-sm-label text-ink-3">
                ดูผลลัพธ์ได้จากตัวอย่างใบเสร็จ
              </p>
            </div>
            <div className="grid gap-4">
              <Field label="หัวใบเสร็จ">
                <Input
                  aria-label="หัวใบเสร็จ"
                  value={form.receiptHeader}
                  onChange={(e) => set('receiptHeader', e.target.value)}
                />
              </Field>
              <Field label="ท้ายใบเสร็จ">
                <Input
                  aria-label="ท้ายใบเสร็จ"
                  value={form.receiptFooter}
                  onChange={(e) => set('receiptFooter', e.target.value)}
                />
              </Field>
            </div>
            <div className="mt-4 flex items-center justify-end gap-3 border-t border-line-2 pt-4">
              {saved ? (
                <span className="font-semibold text-ok text-sm-label">
                  บันทึกแล้ว
                </span>
              ) : null}
              <Btn kind="primary" type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? 'กำลังบันทึก' : 'บันทึกการตั้งค่า'}
              </Btn>
            </div>
          </FormPanel>
        </form>
        <div className="flex justify-center lg:sticky lg:top-6 lg:justify-start">
          <ReceiptPreview s={form} />
        </div>
      </div>
    </div>
  );
}
